import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Container, Row, Col, Card, Button, UncontrolledTooltip } from "reactstrap";
import { useLabTestsService } from "../api/useStudyScreeningTests";
import { showToastMessage } from "common/service/CommonService";
import { BreadcrumbContext } from "Layouts/Header";
import { STUDY_PATHS } from "Routes/paths";
import TruncatedText from "Components/Common/TruncatedText";
import FullPageLoader from "Components/Common/FullPageLoader";
import styles from "./OutOfRangeParametersPage.module.scss";

// Interfaces for Parsed Out-of-Range Data
export interface ParsedParameter {
  parameter: string;
  result: any;
  unit: string | null;
  reference_range: any;
}

export interface ParsedSubCategory {
  sub_category: string;
  parameters: ParsedParameter[];
}

export interface ParsedCategory {
  test_category: string;
  parameters: ParsedParameter[];
  sub_categories: ParsedSubCategory[];
}

// Streamlined Parser for Out-of-Range Parameters JSON (Matches LLM backend output)
export const parseDynamicOutOfRangeJSON = (rawJson: any): ParsedCategory[] => {
  if (!rawJson) return [];

  let data = rawJson;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return [];
    }
  }

  // Unwrap root object if wrapped in outofparameterjson, out_of_range_parameters, or data
  if (data && typeof data === "object" && !Array.isArray(data)) {
    data = data.outofparameterjson || data.out_of_range_parameters || data.data || [];
  }
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return [];
    }
  }

  if (!Array.isArray(data)) return [];

  return data.map((cat: any) => ({
    test_category: String(cat.test_category || cat.category || "General Parameters"),
    parameters: Array.isArray(cat.parameters)
      ? cat.parameters.map((p: any) => ({
        parameter: String(p.parameter || p.parameter_name || p.name || "Parameter"),
        result: p.result ?? p.value ?? "N/A",
        unit: p.unit ?? p.units ?? null,
        reference_range: p.reference_range ?? p.referencerange ?? null,
      }))
      : [],
    sub_categories: Array.isArray(cat.sub_categories)
      ? cat.sub_categories.map((sub: any) => ({
        sub_category: String(sub.sub_category || sub.name || "Sub Category"),
        parameters: Array.isArray(sub.parameters)
          ? sub.parameters.map((p: any) => ({
            parameter: String(p.parameter || p.parameter_name || p.name || "Parameter"),
            result: p.result ?? p.value ?? "N/A",
            unit: p.unit ?? p.units ?? null,
            reference_range: p.reference_range ?? p.referencerange ?? null,
          }))
          : [],
      }))
      : [],
  }));
};

// Universal dynamic Key-Value extractor for arbitrary JSON structures (patient, report, etc.)
export interface KeyValuePair {
  key: string;
  label: string;
  value: string;
}

const formatMetaKeyLabel = (fullKey: string): string => {
  if (!fullKey) return "";

  let cleaned = fullKey;

  // 1. Strip out prefix if delimited by em-dash (—) or hyphen (-)
  if (cleaned.includes("—") || cleaned.includes(" - ")) {
    const parts = cleaned.split(/—|-/);
    if (parts.length > 1) {
      cleaned = parts[parts.length - 1].trim();
    }
  }

  // 2. Take target field if nested dot path (e.g. "patient.name" -> "name")
  if (cleaned.includes(".")) {
    const parts = cleaned.split(".");
    cleaned = parts[parts.length - 1].trim();
  }

  // 3. Convert snake_case & camelCase into spaced words
  const spaced = cleaned
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

  // 4. Capitalize each word dynamically (no hardcoded keys)
  return spaced
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const extractKeyValuePairs = (
  obj: any,
  prefix: string = ""
): KeyValuePair[] => {
  if (!obj || typeof obj !== "object") return [];

  const results: KeyValuePair[] = [];

  const walk = (currentObj: any, currentPrefix: string) => {
    if (!currentObj || typeof currentObj !== "object") return;

    Object.entries(currentObj).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") return;

      const fullKey = currentPrefix ? `${currentPrefix}.${k}` : k;
      const label = formatMetaKeyLabel(fullKey);

      if (typeof v === "object" && !Array.isArray(v)) {
        walk(v, fullKey);
      } else if (!Array.isArray(v)) {
        results.push({
          key: fullKey,
          label,
          value: String(v),
        });
      }
    });
  };

  walk(obj, prefix);
  return results;
};

// Helper to format parameter name nicely (e.g., "ldl_hdl_ratio" -> "LDL / HDL Ratio")
const formatParamName = (name: string): string => {
  if (!name) return "";
  return name
    .split("_")
    .map((word) => {
      const lower = word.toLowerCase();
      if (
        lower === "ldl" ||
        lower === "hdl" ||
        lower === "rbc" ||
        lower === "wbc" ||
        lower === "sgpt" ||
        lower === "alt" ||
        lower === "sgot" ||
        lower === "ast" ||
        lower === "tsh" ||
        lower === "ph"
      ) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

// Helper to render reference range in a compact 1-line EMR layout with tooltip
const renderReferenceRange = (result: any, refRange: any, paramId: string) => {
  if (!refRange) return <span className="text-muted">—</span>;

  if (typeof refRange === "object" && refRange !== null) {
    const entries = Object.entries(refRange);
    if (entries.length === 0) return <span className="text-muted">—</span>;

    // Find primary normal/desirable range key
    const normalEntry =
      entries.find(([k]) => {
        const lower = k.toLowerCase();
        return (
          lower.includes("normal") ||
          lower.includes("desirable") ||
          lower.includes("optimal")
        );
      }) || entries[0];

    const primaryLabel = normalEntry[0].replace(/_/g, " ");
    const primaryVal = String(normalEntry[1]);

    const safeId = `refrange-${paramId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

    return (
      <div className="d-inline-flex align-items-center gap-2">
        <span className={styles.primaryRefRange}>
          <span className="text-muted me-1 text-capitalize fs-12">{primaryLabel}:</span>
          <span className="fw-semibold text-dark font-monospace fs-12">{primaryVal}</span>
        </span>

        {entries.length > 1 && (
          <>
            <span
              id={safeId}
              className={styles.infoIconBtn}
            >
              <i className="ri-information-line fs-13"></i>
            </span>
            <UncontrolledTooltip target={safeId} placement="top">
              <div className="text-start p-1" style={{ minWidth: "160px" }}>
                <div className="fw-bold fs-11 text-uppercase border-bottom pb-1 mb-1 opacity-75">
                  Full Range Spectrum
                </div>
                {entries.map(([k, v], i) => (
                  <div key={i} className="d-flex justify-content-between gap-3 fs-11 py-0.5">
                    <span className="text-capitalize opacity-75">{k.replace(/_/g, " ")}:</span>
                    <span className="font-monospace fw-bold">{String(v)}</span>
                  </div>
                ))}
              </div>
            </UncontrolledTooltip>
          </>
        )}
      </div>
    );
  }

  return (
    <span className="font-monospace text-dark fw-semibold fs-12">
      {String(refRange)}
    </span>
  );
};

const OutOfRangeParametersPage: React.FC = () => {
  const { studylabtestid } = useParams<{ studylabtestid: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<any>(null);

  const rawIsOutNull = searchParams.get("isoutparameternull");
  const isoutparameternull =
    rawIsOutNull !== null ? rawIsOutNull === "true" : undefined;

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  const { SelectMutation } = useLabTestsService();

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST },
      { text: "Study Tests", link: "/study/screening/test" },
      { text: "Out of Range Parameters" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  // Fetch report details using select API on mount / when ID or parameter changes
  useEffect(() => {
    if (studylabtestid) {
      SelectMutation.mutate(
        {
          studylabtestid: Number(studylabtestid),
          isoutparameternull: isoutparameternull,
        },
        {
          onSuccess: (res: any) => {
            setData(res);
          },
          onError: (err: any) => {
            showToastMessage(
              "error",
              err?.message || err || "Failed to fetch lab report details"
            );
          },
        }
      );
    }
  }, [studylabtestid, isoutparameternull]);

  const isLoading = SelectMutation.isPending || (SelectMutation as any).isFetching;

  // Extract select API payload items
  const labData =
    Array.isArray(data?.data) && data.data.length > 0
      ? data.data[0]
      : data?.data || data || {};
  const reportJson = labData?.reportjson || {};
  const outOfParamWrapper = labData?.outofparameterjson || {};

  // Parse out of parameter categories using Universal Dynamic Parser
  const outOfParamCategories = parseDynamicOutOfRangeJSON(outOfParamWrapper);

  const patient = reportJson?.patient || {};
  const reportInfo = reportJson?.report || {};
  const reportName = labData?.reportname || reportInfo?.title || "Lab Report";

  // Dynamically extract all Key-Value pairs from patient & report JSON (no fixed key dependencies)
  const patientKVList = extractKeyValuePairs(patient);
  const reportKVList = extractKeyValuePairs(reportInfo);

  // Calculate total out-of-range parameters count recursively
  let totalOutOfRangeCount = 0;
  outOfParamCategories.forEach((cat) => {
    totalOutOfRangeCount += cat.parameters.length;
    cat.sub_categories.forEach((sub) => {
      totalOutOfRangeCount += sub.parameters.length;
    });
  });

  return (
    <Container fluid className={styles.pageContainer}>
      {/* Top Page Header (Matching Image 1 design) */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-bold text-dark mb-1 fs-18">
            Out of Range Parameters
          </h4>
          <p className="text-muted mb-0 fs-13">
            Manage and review abnormal parameters for lab test report:{" "}
            <strong className="text-dark">{reportName}</strong>
          </p>
        </div>
      </div>

      {isLoading ? (
        <Card className={`${styles.mainWrapperCard} mb-4 position-relative overflow-hidden`} style={{ minHeight: "400px" }}>
          <FullPageLoader message="Loading out-of-range parameters..." />
        </Card>
      ) : (
        /* Single Cohesive Main Card Wrapper */
        <Card className={`${styles.mainWrapperCard} mb-4`}>
          {/* 1. Dynamic Patient & Report Metadata Banner */}
          {(patientKVList.length > 0 || reportKVList.length > 0) && (
            <div className={`${styles.patientBannerSection} p-4 border-bottom`}>
              <Row className="g-4">
                {patientKVList.length > 0 && (
                  <Col lg={reportKVList.length > 0 ? 5 : 12} className={reportKVList.length > 0 ? styles.borderDivider : ""}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className={`${styles.iconCircleMini} ${styles.blue}`}>
                        <i className="ri-user-3-line"></i>
                      </div>
                      <span className={styles.sectionHeaderTitle}>PATIENT DETAILS</span>
                    </div>
                    <div className={styles.metaGridContainer}>
                      {patientKVList.map((item, idx) => (
                        <div key={idx} className={styles.metaGridCell}>
                          <div className={styles.metaCellLabel}>
                            <TruncatedText text={item.label} maxWidth="100%" />
                          </div>
                          <div className={styles.metaCellValue}>
                            <TruncatedText text={item.value} maxWidth="100%" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Col>
                )}

                {reportKVList.length > 0 && (
                  <Col lg={patientKVList.length > 0 ? 7 : 12}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <div className={`${styles.iconCircleMini} ${styles.teal}`}>
                        <i className="ri-file-text-line"></i>
                      </div>
                      <span className={styles.sectionHeaderTitle}>REPORT DETAILS</span>
                    </div>
                    <div className={styles.metaGridContainer}>
                      {reportKVList.map((item, idx) => (
                        <div key={idx} className={styles.metaGridCell}>
                          <div className={styles.metaCellLabel}>
                            <TruncatedText text={item.label} maxWidth="100%" />
                          </div>
                          <div className={styles.metaCellValue}>
                            <TruncatedText text={item.value} maxWidth="100%" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}

          {/* 2. Section Header Bar */}
          {totalOutOfRangeCount > 0 && (
            <div className={`${styles.sectionSearchHeader} p-3 px-4 d-flex justify-content-between align-items-center border-bottom`}>
              <h5 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2 fs-15">
                <i className="ri-flask-line text-warning fs-18"></i>
                <span>Abnormal Lab Test Parameters</span>
              </h5>
            </div>
          )}

          {/* 3. Out of Range Parameters Display List */}
          {outOfParamCategories.length === 0 ? (
            <div className="p-5 text-center">
              <div
                className="mx-auto bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center mb-3"
                style={{ width: "72px", height: "72px" }}
              >
                <i className="ri-checkbox-circle-line fs-36"></i>
              </div>
              <h4 className="fw-bold text-dark">
                All Parameters Within Range
              </h4>
              <p className="text-muted fs-14 mb-0">
                No out-of-range parameters were detected in this lab report.
              </p>
            </div>
          ) : (
            <div className="d-flex flex-column">
              {outOfParamCategories.map((categoryObj, catIdx) => {
                const catName = formatParamName(
                  categoryObj.test_category || `Category ${catIdx + 1}`
                );
                const directParams = categoryObj.parameters;
                const subCategories = categoryObj.sub_categories;

                return (
                  <div key={catIdx} className={styles.categoryBlock}>
                    <div className={styles.categoryHeader}>
                      <h5 className={styles.categoryTitle}>
                        <i className="ri-alert-fill"></i>
                        <span>{catName}</span>
                      </h5>
                    </div>

                    <div className="p-3">
                      {/* Direct Parameters Grid */}
                      {directParams.length > 0 && (
                        <Row className="g-3">
                          {directParams.map((param, pIdx) => (
                            <Col key={pIdx} xs={12} sm={6} md={4} lg={4}>
                              <div className={styles.paramCardTile}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <span className={styles.paramCardName}>
                                    <i className="ri-error-warning-fill me-1 text-danger"></i>
                                    {formatParamName(param.parameter)}
                                  </span>
                                  <span className={styles.resultBadgeMini}>Abnormal</span>
                                </div>

                                <div className="d-flex align-items-baseline gap-1 mb-2 pb-2 border-bottom">
                                  <span className={styles.paramCardValue}>
                                    {param.result ?? "N/A"}
                                  </span>
                                  {param.unit && (
                                    <span className={styles.paramCardUnit}>{param.unit}</span>
                                  )}
                                </div>

                                <div className="d-flex align-items-center justify-content-between pt-1">
                                  <span className={styles.refRangeLabelText}>Ref Range:</span>
                                  {renderReferenceRange(
                                    param.result,
                                    param.reference_range,
                                    `cat-${catIdx}-p-${pIdx}`
                                  )}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      )}

                      {/* Sub Categories */}
                      {subCategories.map((subObj, subIdx) => {
                        const subName = formatParamName(
                          subObj.sub_category || `Sub Category ${subIdx + 1}`
                        );
                        const subParams = subObj.parameters;

                        return (
                          <div key={subIdx} className="mt-3">
                            <div className={`${styles.subCategoryHeader} mb-3`}>
                              <i className="ri-corner-down-right-line"></i>
                              <span>{subName}</span>
                            </div>
                            <Row className="g-3">
                              {subParams.length > 0 ? (
                                subParams.map((param, pIdx) => (
                                  <Col key={pIdx} xs={12} sm={6} md={4} lg={4}>
                                    <div className={styles.paramCardTile}>
                                      <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span className={styles.paramCardName}>
                                          <i className="ri-error-warning-fill me-1 text-danger"></i>
                                          {formatParamName(param.parameter)}
                                        </span>
                                        <span className={styles.resultBadgeMini}>Abnormal</span>
                                      </div>

                                      <div className="d-flex align-items-baseline gap-1 mb-2 pb-2 border-bottom">
                                        <span className={styles.paramCardValue}>
                                          {param.result ?? "N/A"}
                                        </span>
                                        {param.unit && (
                                          <span className={styles.paramCardUnit}>{param.unit}</span>
                                        )}
                                      </div>

                                      <div className="d-flex align-items-center justify-content-between pt-1">
                                        <span className={styles.refRangeLabelText}>Ref Range:</span>
                                        {renderReferenceRange(
                                          param.result,
                                          param.reference_range,
                                          `sub-${catIdx}-${subIdx}-p-${pIdx}`
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                ))
                              ) : null}
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </Container>
  );
};

export default OutOfRangeParametersPage;
